// Challenges on Activity, under Badges: the ones you're in, with a bar and a
// marker for where you'd need to be today, and a picker for the month's
// Khronos challenges plus "Challenge a mate". Opt-in: nothing shows progress
// until you join. Hidden with Show badges off. Rules: api/lib/challenges.js.

import React, { useCallback, useEffect, useState } from "react";
import { ChevronRight, Loader2, Minus, Plus, Trophy } from "lucide-react";
import { Button } from "../ui/button";
import DetailSheet from "../session/generate/DetailSheet";
import { DeleteConfirm, Group } from "../profile/ProfileParts";
import Avatar from "./Avatar";
import * as api from "../../services/api";
import { useSocial } from "../../contexts/SocialContext";
import { useToast } from "../../contexts/ToastContext";
import { cn } from "../../lib/utils";
import { useBadgesShown } from "./Badges";
import {
  amount,
  barGeometry,
  dayLabel,
  daysLeftLabel,
  eyebrow,
  firstName,
  ofTarget,
  pill,
  splitChallenges,
} from "../../utils/challenges";

const row = "grid w-full items-center gap-2.5 px-3 py-2.5 text-left";
const PILL_TONES = {
  ok: "bg-green-500/15 text-green-400",
  warn: "bg-amber-500/15 text-amber-400",
  dim: "bg-muted text-muted-foreground",
};

/** The bar, filling once when it first appears, with the white pace marker. */
function PaceBar({ fill, pace }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setWidth(fill));
    return () => cancelAnimationFrame(id);
  }, [fill]);
  return (
    <div className="relative h-2 rounded-full bg-muted">
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
        style={{ width: `${width}%` }}
      />
      {pace != null && (
        <span
          aria-hidden="true"
          className="absolute -bottom-1 -top-1 w-0.5 -translate-x-1/2 rounded-full bg-foreground"
          style={{ left: `${pace}%` }}
        />
      )}
    </div>
  );
}

/** One challenge you're in: variation A from the mock-up. */
function ChallengeTile({ c, onOpen }) {
  const tag = pill(c);
  const { fill, pace } = barGeometry(c);
  const mate = c.kind === "mate";
  const current = mate ? c.weeksMet || 0 : c.progress?.current ?? 0;
  const title = !mate && c.goal?.unit === "km" ? `${c.title}: ${amount(c.goal.target, "km")} km` : c.title;
  const paceText =
    pace != null ? `You'd be on ${amount(c.pace, c.goal?.unit)} today to finish on time. ` : "";
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${title}. ${amount(current, c.goal?.unit)} ${mate ? `of ${c.weeks} weeks` : ofTarget(c)}. ${paceText}${tag ? tag.label : ""}`}
      className="grid w-full gap-2.5 px-3 py-3 text-left transition-colors hover:bg-muted/30"
    >
      <span className="flex items-center justify-between gap-2">
        <span className="truncate text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          {eyebrow(c)}
        </span>
        {tag && (
          <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", PILL_TONES[tag.tone])}>
            {tag.label}
          </span>
        )}
      </span>
      <span className="text-sm font-semibold">{title}</span>
      {c.status !== "pending" && (
        <>
          <span className="flex items-baseline gap-1.5">
            <span className="font-mono text-3xl font-bold leading-none tabular-nums">{amount(current, c.goal?.unit)}</span>
            <span className="text-xs text-muted-foreground">{mate ? `of ${c.weeks} weeks` : ofTarget(c)}</span>
          </span>
          <PaceBar fill={fill} pace={pace} />
        </>
      )}
      <span className="flex justify-between gap-3 text-xs text-muted-foreground">
        <span className="shrink-0 tabular-nums">
          {mate && c.status === "pending" ? `Waiting for ${firstName(c.mate?.name)}` : daysLeftLabel(c)}
        </span>
        {c.nextStep && <span className="text-right">{c.nextStep}</span>}
      </span>
    </button>
  );
}

/** A mate challenge week by week: dots for each of you. */
function MateWeeks({ c }) {
  if (!c.history?.length) return null;
  const cell = (side, w) => (
    <span
      className={cn(
        "grid h-6 place-items-center rounded-md border text-[11px] tabular-nums",
        side && side.target != null && side.days >= side.target
          ? "border-green-500/50 bg-green-500/20 text-foreground"
          : !w.finished
            ? "border-amber-500/60 text-amber-400"
            : "border-border bg-muted/40 text-muted-foreground",
      )}
    >
      {side ? `${Math.min(7, side.days)}/${side.target ?? "–"}` : ""}
    </span>
  );
  return (
    <div
      className="grid items-center gap-x-2 gap-y-1.5 text-xs"
      style={{ gridTemplateColumns: `auto repeat(${c.weeks}, minmax(0, 1fr))` }}
    >
      <span />
      {Array.from({ length: c.weeks }, (_, i) => (
        <span key={i} className="text-center text-[11px] text-muted-foreground">
          Wk {i + 1}
        </span>
      ))}
      <span className="pr-1 text-muted-foreground">You</span>
      {Array.from({ length: c.weeks }, (_, i) => (
        <React.Fragment key={i}>{c.history[i] ? cell(c.history[i].me, c.history[i]) : <span />}</React.Fragment>
      ))}
      <span className="pr-1 text-muted-foreground">{firstName(c.mate?.name)}</span>
      {Array.from({ length: c.weeks }, (_, i) => (
        <React.Fragment key={i}>{c.history[i] ? cell(c.history[i].them, c.history[i]) : <span />}</React.Fragment>
      ))}
    </div>
  );
}

export default function ChallengesCard() {
  const shown = useBadgesShown();
  const { friends = [], challengeVersion } = useSocial();
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mateOpen, setMateOpen] = useState(false);
  const [weeks, setWeeks] = useState(4);
  const [detail, setDetail] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.getChallenges());
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    if (shown) load();
  }, [load, shown, challengeVersion]);

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      setData(await fn());
      if (message) showToast({ title: message });
      return true;
    } catch (e) {
      showToast({ title: "That didn't work", description: e.message });
      return false;
    } finally {
      setBusy(null);
    }
  };

  if (!shown || !data) return null;
  const { mine, invites, open } = splitChallenges(data);
  const catalogue = data.challenges || [];
  const current = detail && [...(data.challenges || []), ...(data.mates || [])].find((c) => c.kind === detail.kind && c.id === detail.id);

  return (
    <>
      <Group title="Challenges">
        {invites.map((c) => (
          <div key={`i${c.id}`} className={`${row} grid-cols-[2rem_minmax(0,1fr)_auto]`}>
            <Avatar name={c.mate?.name} photo={c.mate?.photo_url} />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{firstName(c.mate?.name)} challenged you</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Both hit your own target for {c.weeks} weeks
              </span>
            </span>
            <span className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground"
                disabled={busy === c.id}
                onClick={() => act(c.id, () => api.leaveChallenge(c.id))}
              >
                Decline
              </Button>
              <Button
                size="sm"
                disabled={busy === c.id}
                onClick={() => act(c.id, () => api.joinChallenge(c.id), "You're on. It starts Monday.")}
              >
                Accept
              </Button>
            </span>
          </div>
        ))}
        {mine.map((c) => (
          <ChallengeTile key={`${c.kind}${c.id}`} c={c} onOpen={() => setDetail({ kind: c.kind, id: c.id })} />
        ))}
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className={`${row} grid-cols-[1.25rem_minmax(0,1fr)_1rem] transition-colors hover:bg-muted/30`}
        >
          <Trophy className="h-4 w-4 text-primary" />
          <span className="min-w-0">
            <span className="block text-sm font-medium">
              {mine.length ? "More challenges" : "Take on a challenge"}
            </span>
            <span className="mt-0.5 block truncate text-xs text-muted-foreground">
              {open.length
                ? `${open.length} from Khronos${friends.length ? ", or challenge a mate" : ""}`
                : friends.length
                  ? "Challenge a mate"
                  : "New ones each month"}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      </Group>

      <DetailSheet open={pickerOpen} onOpenChange={setPickerOpen} title="Challenges">
        <p className="text-[13px] leading-snug text-muted-foreground">
          Every session in a challenge&rsquo;s dates counts, even from before you join. Finish one for a
          limited-edition badge.
        </p>
        {catalogue.length > 0 ? (
          <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
            {catalogue.map((c) => (
              <div key={c.id} className={`${row} grid-cols-[minmax(0,1fr)_auto]`}>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{c.title}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {c.description}
                    {c.phase === "upcoming" ? ` · from ${dayLabel(c.starts)}` : ""}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  {c.joined != null && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                      {c.joined} joined
                    </span>
                  )}
                  {c.status === "open" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy === c.id || c.phase === "ended"}
                      onClick={() => act(c.id, () => api.joinChallenge(c.id), `Joined ${c.title}`)}
                    >
                      {busy === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join"}
                    </Button>
                  ) : (
                    <span className="text-xs font-semibold text-primary">
                      {c.status === "completed" ? "Done" : "Joined"}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No Khronos challenges running right now. New ones each month.</p>
        )}
        {friends.length > 0 && (
          <Button
            onClick={() => {
              setPickerOpen(false);
              setMateOpen(true);
            }}
          >
            Challenge a mate
          </Button>
        )}
      </DetailSheet>

      <DetailSheet
        open={mateOpen}
        onOpenChange={setMateOpen}
        title="Challenge a mate"
        onBack={() => {
          setMateOpen(false);
          setPickerOpen(true);
        }}
      >
        <p className="text-[13px] leading-snug text-muted-foreground">
          You each train to your own weekly target from Profile, locked in every Monday. You both need to hit
          yours every week. It starts the Monday after they accept.
        </p>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background px-3 py-2.5">
          <span className="text-sm font-medium">How long</span>
          <span className="flex items-center gap-2">
            <Button
              size="icon"
              variant="outline"
              className="h-9 w-9"
              aria-label="Fewer weeks"
              disabled={weeks <= 1}
              onClick={() => setWeeks((w) => Math.max(1, w - 1))}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-16 text-center text-sm font-semibold tabular-nums" aria-live="polite">
              {weeks} {weeks === 1 ? "week" : "weeks"}
            </span>
            <Button
              size="icon"
              variant="outline"
              className="h-9 w-9"
              aria-label="More weeks"
              disabled={weeks >= 12}
              onClick={() => setWeeks((w) => Math.min(12, w + 1))}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </span>
        </div>
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
          {friends.map((f) => (
            <button
              key={f.id}
              type="button"
              disabled={Boolean(busy)}
              onClick={async () => {
                const ok = await act(`f${f.id}`, () => api.challengeMate(f.id, weeks), `Challenge sent to ${firstName(f.name)}`);
                if (ok) setMateOpen(false);
              }}
              className={`${row} grid-cols-[2rem_minmax(0,1fr)_auto] transition-colors hover:bg-muted/30`}
            >
              <Avatar name={f.name} photo={f.photo_url} />
              <span className="truncate text-sm font-medium">{f.name}</span>
              {busy === `f${f.id}` ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <span className="text-xs font-semibold text-primary">Challenge</span>
              )}
            </button>
          ))}
        </div>
      </DetailSheet>

      {current && (
        <DetailSheet
          open={Boolean(current)}
          onOpenChange={(o) => {
            if (!o) {
              setDetail(null);
              setConfirming(false);
            }
          }}
          title={current.kind === "mate" ? `Challenge with ${firstName(current.mate?.name)}` : current.title}
        >
          {current.kind === "mate" ? (
            <>
              <MateWeeks c={current} />
              <ul className="grid gap-1.5 text-[13px] leading-snug text-muted-foreground">
                <li>A week counts when you both hit your own target. Weeks run Monday to Sunday.</li>
                <li>Targets are locked in on Monday. Change yours in Profile and it applies from next week.</li>
                <li>Miss a week and the challenge ends. Make every week and you both get the badge.</li>
              </ul>
            </>
          ) : (
            <ul className="grid gap-1.5 text-[13px] leading-snug text-muted-foreground">
              <li>
                {current.description}, {dayLabel(current.starts)} to {dayLabel(current.ends)}.
              </li>
              {current.phase === "running" && current.pace != null && (
                <li>
                  The white mark is where you&rsquo;d be today to finish on time: {amount(current.pace, current.goal?.unit)}{" "}
                  {current.goal?.unit}.
                </li>
              )}
              <li>Badge: {current.badge?.title}</li>
            </ul>
          )}
          {["joined", "active", "pending"].includes(current.status) && (
            <DeleteConfirm
              action={current.kind === "mate" ? (current.status === "pending" ? "Cancel" : "Stop") : "Leave"}
              name={current.kind === "mate" ? "this challenge" : current.title}
              consequence={
                current.kind === "mate"
                  ? "It ends for both of you. Badges you've earned stay."
                  : "Your sessions still count if you join again."
              }
              confirming={confirming}
              onAsk={() => setConfirming(true)}
              onCancel={() => setConfirming(false)}
              onConfirm={async () => {
                const ok = await act("leave", () => api.leaveChallenge(current.id));
                if (ok) {
                  setDetail(null);
                  setConfirming(false);
                }
              }}
              busy={busy === "leave"}
            />
          )}
        </DetailSheet>
      )}
    </>
  );
}
